import { useEffect, useState } from "react";
import { cleanArticle } from "@/lib/articleAi.functions";

const inflight = new Map<string, Promise<string | null>>();
const memoryCache = new Map<string, string>();

const STORAGE_PREFIX = "clean-article:";

const readSession = (id: string): string | null => {
  const memoryValue = memoryCache.get(id);
  if (memoryValue) return memoryValue;
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + id);
    if (stored && stored.length > 200) {
      memoryCache.set(id, stored);
      return stored;
    }
  } catch {
    /* sessionStorage unavailable */
  }
  return null;
};

const writeSession = (id: string, content: string) => {
  memoryCache.set(id, content);
  try {
    sessionStorage.setItem(STORAGE_PREFIX + id, content);
  } catch {
    /* quota or unavailable, memory cache still applies */
  }
};

/**
 * Universal AI article cleaner.
 * Prefers the cleaned body already stored on the article row, then a
 * session cache, and only calls the `clean-article` edge function when no
 * cleaned version exists yet. Returns null so callers can fall back to the
 * deterministic DOM cleaner.
 */
export function useCleanArticleContent(
  articleId?: string,
  rawContent?: string | null,
  storedCleaned?: string | null
) {
  const [cleaned, setCleaned] = useState<string | null>(() => {
    if (storedCleaned && storedCleaned.length > 200) return storedCleaned;
    return articleId ? readSession(articleId) : null;
  });

  useEffect(() => {
    let cancelled = false;

    if (!articleId) {
      setCleaned(null);
      return;
    }

    // 1. Already cleaned server-side and delivered with the article row.
    if (storedCleaned && storedCleaned.length > 200) {
      writeSession(articleId, storedCleaned);
      setCleaned(storedCleaned);
      return;
    }

    // 2. Cleaned earlier in this browsing session.
    const cachedForSession = readSession(articleId);
    if (cachedForSession) {
      setCleaned(cachedForSession);
      return;
    }

    setCleaned(null);

    if (!rawContent || rawContent.trim().length < 400) return;

    // 3. First time: ask the edge function once (deduplicated).
    const run =
      inflight.get(articleId) ??
      (async () => {
        const data = await cleanArticle({ data: { articleId } }).catch(() => null);
        const content = (data as { content?: string | null } | null)?.content ?? null;
        return content && content.length > 200 ? content : null;
      })();

    inflight.set(articleId, run);

    run
      .then((content) => {
        if (content) writeSession(articleId, content);
        if (!cancelled) setCleaned(content);
      })
      .catch(() => undefined)
      .finally(() => {
        inflight.delete(articleId);
      });

    return () => {
      cancelled = true;
    };
  }, [articleId, rawContent, storedCleaned]);

  return cleaned;
}
